import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { List, X, ArrowRight, Globe, Phone, Envelope, User } from '@phosphor-icons/react';
import ThemeToggle from './ThemeToggle';
import BrandLockup from './BrandLockup';

const Navbar = ({ isDarkMode, toggleTheme }) => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About Us' },
    { to: '/services', label: 'Services' },
    { to: '/internship', label: 'Internship' },
    { to: '/contact', label: 'Contact Us' },
  ];

  const isActive = (to) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  return (
    <>
      {/* Enhanced Navigation */}
      <nav className={`fixed top-0 w-full z-50 smooth-transition ${isScrolled || !isHome
        ? 'glass-panel shadow-medium'
        : 'bg-transparent'
        }`}>
        <div className="section-max-width px-4 md:px-6 py-4 flex justify-between items-center">
          <BrandLockup size="md" />

          {/* Enhanced Desktop Links */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`nav-link relative px-4 py-2 text-sm font-medium rounded-lg smooth-transition ${
                  isActive(link.to)
                    ? 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20'
                    : 'text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-gray-50 dark:hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}

            <ThemeToggle isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

            <Link
              to="/admin"
              className="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 hover:bg-brand-50 dark:hover:bg-brand-900/30 hover:text-brand-600 dark:hover:text-brand-400 hover:border-brand-300 smooth-transition flex items-center justify-center"
              aria-label="Login"
              title="Login"
            >
              <User className="w-5 h-5" weight="bold" />
            </Link>
          </div>

          {/* Enhanced Mobile Actions */}
          <div className="flex items-center gap-3 lg:hidden">
            <ThemeToggle isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

            <Link
              to="/admin"
              className="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 hover:bg-brand-50 dark:hover:bg-brand-900/30 hover:text-brand-600 smooth-transition flex items-center justify-center"
              aria-label="Login"
              title="Login"
            >
              <User className="w-5 h-5" weight="bold" />
            </Link>

            <button
              onClick={() => setIsMenuOpen(true)}
              className="p-3 rounded-xl bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-white/20 smooth-transition"
              aria-label="Open menu"
            >
              <List className="text-xl text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>
      </nav>

      {/* Enhanced Mobile Drawer */}
      <div
        className={`fixed inset-0 z-[60] smooth-transition lg:hidden ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-md"
          onClick={() => setIsMenuOpen(false)}
        />
        <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white dark:bg-dark-800 shadow-2xl p-6 flex flex-col transform transition-transform duration-300">
          {/* Mobile Header */}
          <div className="flex justify-between items-center mb-8 pb-6 border-b border-gray-200 dark:border-gray-600">
            <div>
              <span className="font-display font-bold text-xl text-gray-900 dark:text-white">
                Menu
              </span>
            </div>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 smooth-transition"
              aria-label="Close menu"
            >
              <X className="text-xl text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          {/* Mobile Navigation Links */}
          <div className="flex flex-col gap-2 mb-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsMenuOpen(false)}
                className={`flex items-center justify-between p-4 rounded-xl smooth-transition ${
                  isActive(link.to)
                    ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-brand-600 dark:hover:text-brand-400'
                }`}
              >
                <span className="font-medium">{link.label}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            ))}
          </div>

          {/* Mobile Contact Info */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-6 space-y-4">
            <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
              <Phone className="w-5 h-5 text-brand-500" />
              <span className="text-sm">7350695792/9022487236</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
              <Envelope className="w-5 h-5 text-brand-500" />
              <span className="text-sm">info@growmoreitservices.com</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
              <Globe className="w-5 h-5 text-brand-500" />
              <span className="text-sm">www.growmoreitservices.com</span>
            </div>
          </div>

          {/* Mobile Login */}
          <div className="mt-auto pt-6">
            <Link
              to="/admin"
              onClick={() => setIsMenuOpen(false)}
              className="w-full py-4 rounded-xl bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 text-gray-800 dark:text-white font-semibold flex items-center justify-center gap-2 hover:bg-brand-50 dark:hover:bg-brand-900/30 hover:text-brand-600 smooth-transition"
            >
              <User className="w-5 h-5" weight="bold" />
              Login
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
